/**
 * File open/save using File System Access API with download fallback
 * (PROJECT §8 NppIO / WEB_IMPLEMENTATION_LAYER §6–7).
 */

import { recognizedSourceFileExtensions } from "../doc/inferLanguageFromFilename.js";
import { xlsFileToNppJsonString, xlsxFileToNppJsonString } from "./excelWorkbook.js";
import { isExcelViewFile } from "./excelViewFile.js";
import { isImageViewFile } from "./imageViewFile.js";
import { shouldTreatFileAsPdf } from "./pdfViewFile.js";
import { renderLibraryNmPreview } from "./libraryNmPreview.js";

const OPEN_EXTENSIONS = [...recognizedSourceFileExtensions()];

const IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".jfif", ".webp", ".gif", ".bmp", ".svg", ".ico", ".avif", ".heic", ".heif"];

const PDF_EXTENSIONS = [".pdf"];

const EXCEL_EXTENSIONS = [".xls", ".xlsx", ".xlsm"];

export type OpenEditorFileResult =
  | { kind: "text"; name: string; file: File; text: string; handle: FileSystemFileHandle | null }
  | { kind: "image"; name: string; file: File; handle: FileSystemFileHandle | null }
  | { kind: "pdf"; name: string; file: File; handle: FileSystemFileHandle | null };

export async function readFileAsText(
  file: File,
  pathHint?: string | null,
  handleNameHint?: string | null,
): Promise<string> {
  const preview = await renderLibraryNmPreview(file);
  if (preview !== null) return preview;
  if (isImageViewFile(file)) return "";
  if (await shouldTreatFileAsPdf(file, pathHint, handleNameHint)) return "";
  if (isExcelViewFile(file)) {
    return /\.xls$/i.test(file.name) ? xlsFileToNppJsonString(file) : xlsxFileToNppJsonString(file);
  }
  return file.text();
}

export async function openEditorFile(): Promise<OpenEditorFileResult | null> {
  if ("showOpenFilePicker" in window) {
    try {
      const [handle] = await window.showOpenFilePicker({
        multiple: false,
        types: [
          {
            description: "Text and source files",
            accept: { "text/*": OPEN_EXTENSIONS },
          },
          {
            description: "Images",
            accept: { "image/*": IMAGE_EXTENSIONS },
          },
          {
            description: "PDF",
            accept: { "application/pdf": PDF_EXTENSIONS },
          },
          {
            description: "Excel workbook",
            accept: {
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
              "application/vnd.ms-excel.sheet.macroEnabled.12": [".xlsm"],
              "application/vnd.ms-excel": [".xls"],
            },
          },
        ],
      });
      const file = await handle.getFile();
      const pathHint =
        file.webkitRelativePath?.replace(/\\/g, "/").trim() || handle.name || file.name;
      const displayName = handle.name || file.name;
      if (isImageViewFile(file)) {
        return { kind: "image", name: displayName, file, handle };
      }
      if (await shouldTreatFileAsPdf(file, pathHint, handle.name)) {
        return { kind: "pdf", name: displayName, file, handle };
      }
      return { kind: "text", name: displayName, file, text: await readFileAsText(file, pathHint, handle.name), handle };
    } catch (e) {
      if ((e as DOMException).name === "AbortError") return null;
      throw e;
    }
  }
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = [
      "text/*",
      ...OPEN_EXTENSIONS,
      "image/*",
      ...IMAGE_EXTENSIONS,
      "application/pdf",
      ...PDF_EXTENSIONS,
      ...EXCEL_EXTENSIONS,
    ].join(",");
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) {
        resolve(null);
        return;
      }
      const pathHint = file.webkitRelativePath?.replace(/\\/g, "/").trim() || file.name;
      if (isImageViewFile(file)) {
        resolve({ kind: "image", name: file.name, file, handle: null });
        return;
      }
      if (await shouldTreatFileAsPdf(file, pathHint, null)) {
        resolve({ kind: "pdf", name: file.name, file, handle: null });
        return;
      }
      resolve({ kind: "text", name: file.name, file, text: await readFileAsText(file, pathHint, null), handle: null });
    };
    input.oncancel = () => resolve(null);
    input.click();
  });
}

export async function saveTextAsDownload(filename: string, text: string): Promise<void> {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export async function saveWithPicker(
  suggestedName: string,
  text: string,
  existingHandle: FileSystemFileHandle | null,
): Promise<FileSystemFileHandle | null> {
  if ("showSaveFilePicker" in window) {
    const types: FilePickerAcceptType[] = [{ description: "Text", accept: { "text/plain": [".txt"] } }];
    try {
      const handle = await window.showSaveFilePicker({ suggestedName, types });
      const w = await handle.createWritable();
      await w.write(text);
      await w.close();
      return handle;
    } catch (e) {
      if ((e as DOMException).name === "AbortError") return null;
      throw e;
    }
  }
  await saveTextAsDownload(suggestedName, text);
  return null;
}

export async function writeToExistingHandle(
  handle: FileSystemFileHandle,
  text: string,
): Promise<void> {
  const w = await handle.createWritable();
  await w.write(text);
  await w.close();
}

export async function writeBinaryToExistingHandle(
  handle: FileSystemFileHandle,
  data: ArrayBuffer,
): Promise<void> {
  const w = await handle.createWritable();
  await w.write(new Uint8Array(data));
  await w.close();
}

/** Save-as / pick handle for `.xlsx` bytes (Excel workbook). */
export async function saveExcelWithPicker(
  suggestedName: string,
  data: ArrayBuffer,
  _existingHandle: FileSystemFileHandle | null,
): Promise<FileSystemFileHandle | null> {
  const name = /\.xlsx$/i.test(suggestedName) ? suggestedName : `${suggestedName.replace(/\.(xlsm|txt)$/i, "")}.xlsx`;
  if ("showSaveFilePicker" in window) {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: name,
        types: [
          {
            description: "Excel workbook",
            accept: {
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
            },
          },
        ],
      });
      await writeBinaryToExistingHandle(handle, data);
      return handle;
    } catch (e) {
      if ((e as DOMException).name === "AbortError") return null;
      throw e;
    }
  }
  const blob = new Blob([data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
  return null;
}
